import { yupResolver } from "@hookform/resolvers/yup";
import { ChevronLeftIcon, EnterIcon } from "@radix-ui/react-icons";
import { Box, Button, Card, Flex, RadioGroup, Spinner, Text } from "@radix-ui/themes";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import SelectComponent from "../../components/inputs/Select/SelectComponent";
import TextFieldComponent from "../../components/inputs/Textfield/TextfieldComponent";
import { useGetScientificAreasQuery } from "../../hooks/useGetScientificAreasQuery";
import { useGetSubjectsQuery } from "../../hooks/useGetSubjectsQuery";
import { routes } from "../../routes";
import { createSubject } from "../../services/school/subject/createSubject";
import styles from '../../styles/CreateUser.module.scss';
import { CreateSubjectType } from "../../types/Subject";
import { CreateSubjectFormValidation } from "./CreateSubjectFormValidation";
import RadioGroupComponent from "../../components/inputs/RadioGroup/RadioGroupComponent";

export const CreateSubject = () => {
  const navigate = useNavigate();
  const defaultValues: CreateSubjectType = {
    description: '',
    isCore: false,
    hasExam: false,
    isMonograph: false,
    idScientificArea: "",
    idPrecedence: undefined
  }
  const { control, handleSubmit, setValue, getValues, formState: { errors } } = useForm<CreateSubjectType>({
    resolver: yupResolver(CreateSubjectFormValidation),
    defaultValues,
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldUseNativeValidation: false,
  });
  const mutation = useMutation({ mutationFn: createSubject, mutationKey: 'createSubject' });
  const { data: scientificAreas, isLoading: isLoadingScientificAreas, isError: errorScientificAreas } = useGetScientificAreasQuery({ PageNumber: 1, PageSize: 400 });
  const { data: subjects, isLoading: isLoadingSubjects, isError: errorSubjects, refetch: refreshSubjects } = useGetSubjectsQuery({ PageNumber: 1, PageSize: 400, idScientificArea: getValues('idScientificArea') },
    { enabled: !!getValues('idScientificArea') });

  const onSubmit = handleSubmit(async (data) => {
    try {

      const result = await mutation.mutateAsync({ ...data, idPrecedence: data?.idPrecedence ?? undefined });
      if (result) {
        toast.success("Disciplina criada");
        navigate(routes.subjects);
      }
    } catch (err: any) {
      toast.error(`${mutation.error ?? err?.message}`);
    }
  });
  const handleBack = () => {
    navigate(routes.subjects);
  }
  const handleScientificAreaChange = (idScientificArea: string) => {
    setValue('idScientificArea', idScientificArea);
    refreshSubjects();
  };

  return (
    <form onSubmit={onSubmit} noValidate>
      <Card size='1'>
        <Flex direction='column' gap='4'>
          <TextFieldComponent name="description" label='Descrição' control={control} errors={errors} fieldProps={{ placeholder: 'Descrição' }} required={true} />
          <SelectComponent name="idScientificArea" label='Área Científica' control={control} errors={errors} style={{ width: '100%' }}
            options={scientificAreas?.items.map(scientificArea => ({
              value: scientificArea.id ?? "",
              label: scientificArea.description ?? ""
            }))}
            onSelect={handleScientificAreaChange}
            required={true}
          />

          <SelectComponent name="idPrecedence" label="Precedência" control={control} errors={errors} style={{ width: '100%' }}
            options={subjects?.items.map(subject => ({
              value: subject.id ?? "",
              label: subject.description ?? ""
            }))}
          />

          <RadioGroupComponent name="isCore" label="Tipo de disciplina" control={control} errors={errors} style={{ width: '100%' }}
            options={[
              { value: "true", label: "Nuclear" }
              , { value: "false", label: "Opcional" }]}
            fieldProps={{
              style: { display: 'flex', flexDirection: 'row', gap: '16px' }
            }}
          />

          <RadioGroupComponent name="hasExam" label="Tem exame?" control={control} errors={errors} style={{ width: '100%' }}
            options={[
              { value: "true", label: "Sim" }
              , { value: "false", label: "Não" }]}
            fieldProps={{
              style: { display: 'flex', flexDirection: 'row', gap: '16px' }
            }}
          />
          <RadioGroupComponent name="isMonograph" label="É Monografia" control={control} errors={errors} style={{ width: '100%' }}
            options={[
              { value: "true", label: "Sim" }
              , { value: "false", label: "Não" }]}
            fieldProps={{
              style: { display: 'flex', flexDirection: 'row', gap: '16px' }
            }}
          />

          <Flex direction='row' gap='4' style={{ alignSelf: 'end' }}>
            <Button color='red' onClick={handleBack} disabled={mutation.isLoading}>
              <ChevronLeftIcon /> voltar
            </Button>
            <Button type="submit" disabled={mutation.isLoading} variant='surface'>
              {mutation.isLoading ? <Spinner loading> <EnterIcon /></Spinner> : <EnterIcon />}
              Registar
            </Button>
          </Flex>
        </Flex>
      </Card>
    </form>
  );
}
